/**
 * O que faz: sugere significados explícitos de colunas com exemplos locais.
 * Onde usa: configuração do Kanban, sem exigir prompt da clínica.
 * Conexões: organizacao-contratos.ts e reexport frontend; não usa IA ou rede.
 * Tabelas: nenhuma; sugerir não cria nem confirma coluna.
 * Data: 20/10/2026.
 */
import type { CondicaoColuna, ExemploColuna, ExigenciaRegistro, SugestaoRegraLocal } from './organizacao-contratos.ts';

export const CATALOGO_CONDICOES: Record<CondicaoColuna, {
  rotulo: string; descricao: string; exclusoes: string[]; exemplos: ExemploColuna[];
}> = {
  interesse: {
    rotulo: 'Demonstra interesse',
    descricao: 'O paciente demonstra interesse próprio em um tratamento, sem discutir valores ou pedir agendamento.',
    exclusoes: ['Abertura pronta de anúncio sem resposta própria.', 'Oferta da equipe sem aceite do paciente.'],
    exemplos: [{ texto: 'Quero entender como funciona o implante.', corresponde: true }, { texto: 'Olá, vi o anúncio e quero informações.', corresponde: false }],
  },
  preco: {
    rotulo: 'Discute valores',
    descricao: 'O paciente pergunta ou negocia o preço ou as formas de pagamento do tratamento.',
    exclusoes: ['Preço oferecido somente pela equipe, sem manifestação do paciente.'],
    exemplos: [{ texto: 'Quanto custa e pode parcelar?', corresponde: true }],
  },
  quer_marcar: {
    rotulo: 'Quer marcar',
    descricao: 'O paciente pede para marcar avaliação ou informa disponibilidade para agendar.',
    exclusoes: ['Perguntar como funciona a avaliação não é pedir agendamento.', 'Querer marcar não comprova consulta registrada.'],
    exemplos: [{ texto: 'Podemos marcar minha avaliação na terça?', corresponde: true }],
  },
  retomar: {
    rotulo: 'Pede contato depois',
    descricao: 'O paciente pede explicitamente que a clínica volte a falar com ele em outro momento.',
    exclusoes: ['Silêncio ou ausência de resposta não são pedido de retorno.', 'Qualquer coisa eu aviso não é pedido de contato.'],
    exemplos: [{ texto: 'Me procure de novo no mês que vem.', corresponde: true }],
  },
  personalizado: {
    rotulo: 'Explicar com minhas palavras',
    // Descrição vazia obriga a escolha de significado, em vez de adivinhar pelo título.
    descricao: '', exclusoes: [], exemplos: [],
  },
  fato_sistema: {
    rotulo: 'Somente pelos registros do CRM',
    descricao: 'Usar somente quando o registro correspondente existir no CRM.',
    exclusoes: ['Mensagens de venda ou presença não substituem registros confirmados.'],
    exemplos: [],
  },
};

export const COLUNAS_INICIAIS: Array<{ nome: string; condicao: CondicaoColuna }> = [
  { nome: 'Conhecendo o tratamento', condicao: 'interesse' },
  { nome: 'Avaliando valores', condicao: 'preco' },
  { nome: 'Quer marcar avaliação', condicao: 'quer_marcar' },
  { nome: 'Retomar depois', condicao: 'retomar' },
];

const normalizarNome = (nome: string) => nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().replace(/\s+/g, ' ').toLowerCase();
const NOMES_EXPLICITOS: Record<string, { condicao: CondicaoColuna; exigeRegistro: ExigenciaRegistro }> = {
  'conhecendo o tratamento': { condicao: 'interesse', exigeRegistro: null },
  'avaliando valores': { condicao: 'preco', exigeRegistro: null },
  'quer marcar avaliacao': { condicao: 'quer_marcar', exigeRegistro: null },
  'retomar depois': { condicao: 'retomar', exigeRegistro: null },
  'agendados': { condicao: 'fato_sistema', exigeRegistro: 'agenda' },
  'agendado': { condicao: 'fato_sistema', exigeRegistro: 'agenda' },
  'compareceu': { condicao: 'fato_sistema', exigeRegistro: 'comparecimento' },
  'vendido': { condicao: 'fato_sistema', exigeRegistro: 'venda' },
  'vendidos': { condicao: 'fato_sistema', exigeRegistro: 'venda' },
};

export function sugerirRegraLocal(nome: string): SugestaoRegraLocal {
  // Só nomes completos conhecidos recebem sugestão; nenhuma sugestão confirma uma regra.
  const nomeNormalizado = normalizarNome(nome);
  // Nomes vindos da clínica não podem acessar propriedades herdadas, como constructor.
  if (!Object.hasOwn(NOMES_EXPLICITOS, nomeNormalizado)) {
    return { condicao: null, descricao: '', exigeRegistro: null, precisaEsclarecimento: true };
  }
  const conhecida = NOMES_EXPLICITOS[nomeNormalizado];
  return { ...conhecida, descricao: CATALOGO_CONDICOES[conhecida.condicao].descricao, precisaEsclarecimento: false };
}
